import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ToastModule } from 'primeng/toast';

import { ActionPlansService } from '../../services/action-plans/action-plans.service';

@Component({
  selector: 'app-add-update-action-plan',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    ButtonModule,
    InputTextareaModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './add-update-action-plan.component.html',
  styleUrls: ['./add-update-action-plan.component.scss']
})
export class AddUpdateActionPlanComponent implements OnInit {
  private _actionPlansService = inject(ActionPlansService);
  private _messageService = inject(MessageService);
  private _formBuilder = inject(FormBuilder);
  public config = inject(DynamicDialogConfig);
  public ref = inject(DynamicDialogRef);

  public actionPlanForm: FormGroup;
  public rootCauses: any[] = [];
  public qualifications: any[] = [];
  public events: any[] = [];

  constructor() {
    this.actionPlanForm = this._formBuilder.group({
      root_cause: ['', [Validators.required]],
      event: ['', [Validators.required]],
      description: [''],
      qualification: ['', [Validators.required]],
      id: ['']
    });
  }

  ngOnInit(): void {
    this.loadData();

    if (this.config.data.actionPlan) {
      this.patchForm();
    }
  }

  loadData(): void {
    this._actionPlansService.getRootCauses().subscribe((data: any) => {
      this.rootCauses = data.items;
    });

    this._actionPlansService.getQualifications().subscribe((data: any) => {
      this.qualifications = data.items;
    });

    this._actionPlansService.getEvents().subscribe((data: any) => {
      this.events = data.items;
    });
  }

  filterRootCause(event: any) {
    const query = event ? event.query.toLowerCase() : '';

    this._actionPlansService.getRootCauses(query).subscribe((data: any) => {
      this.rootCauses = data.items;
    });
  }

  filterQualification(event: any) {
    const query = event ? event.query.toLowerCase() : '';

    this._actionPlansService.getQualifications(query).subscribe((data: any) => {
      this.qualifications = data.items;
    });
  }

  filterEvent(event: any) {
    const query = event ? event.query.toLowerCase() : '';

    this._actionPlansService.getEvents(query).subscribe((data: any) => {
      this.events = data.items;
    });
  }

  patchForm() {
    let item = this.config.data.actionPlan;

    this.actionPlanForm.patchValue({
      root_cause: item.root_cause,
      event: item.event,
      description: item.description,
      qualification: item.qualification,
      id: item.id
    });
  }

  accept() {
    if (this.actionPlanForm.invalid) {
      this.actionPlanForm.markAllAsTouched();
      this._messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Por favor, complete todos los campos requeridos'
      });
      return;
    }

    if (!this.config.data.actionPlan) {
      this.save();
    } else {
      this.update();
    }
  }

  cancel() {
    this.ref.close();
  }

  save() {
    const data = {
      root_cause: this.actionPlanForm.value.root_cause.id,
      event: this.actionPlanForm.value.event?.id || this.actionPlanForm.value.event,
      description: this.actionPlanForm.value.description,
      qualification: this.actionPlanForm.value.qualification.id
    };

    this._actionPlansService.createActionPlan(data).subscribe({
      next: (res) => {
        this._messageService.add({
          severity: 'success',
          summary: '¡Éxito!',
          detail: 'Plan de acción creado correctamente'
        });
        this.ref.close(true);
      },
      error: (err) => {
        console.error('Error al crear el plan de acción', err);
        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al crear el plan de acción'
        });
      }
    });
  }

  update() {
    const data = {
      root_cause: this.actionPlanForm.value.root_cause.id,
      event: this.actionPlanForm.value.event?.id || this.actionPlanForm.value.event,
      description: this.actionPlanForm.value.description,
      qualification: this.actionPlanForm.value.qualification.id
    };

    this._actionPlansService.updateActionPlan(data, this.actionPlanForm.value.id).subscribe({
      next: (res) => {
        this._messageService.add({
          severity: 'success',
          summary: '¡Éxito!',
          detail: 'Plan de acción actualizado correctamente'
        });
        this.ref.close(true);
      },
      error: (err) => {
        console.error('Error al actualizar el plan de acción', err);
        this._messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar el plan de acción'
        });
      }
    });
  }
}
